// Shared by Scoreboard.vue and its modals. A metric looks like the API's (routes/scoreboard.ts GET /):
// { ID, Team, Name, OwnerEmployeeID, Unit, Definition, Goals: [{ FromWeek, Cmp, Goal, GoalMax }],
// Values: { 'YYYY-MM-DD': number } }. Weeks are keyed by the Monday they start on, in local time.

const MONTHS = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];
export const DAY_NAMES = [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ];
export const WEEK_COUNT = 13;
// The group everyone can open on the Scorecard and Rocks tabs, stored under a reserved key so it never
// clashes with a configured team (API employeeTeams.ts COMPANY_TEAM). Show it with teamLabel.
export const COMPANY_TEAM = '__company__';

// A team's display name (a global template helper, main.ts)
export function teamLabel(team: string): string {
    return team == COMPANY_TEAM ? 'Company' : team;
}

function iso(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parse(week: string): Date {
    const [ y, m, d ] = week.split('-').map(Number);
    return new Date(y, m - 1, d);
}

export function addDays(week: string, days: number): string {
    const d = parse(week);
    d.setDate(d.getDate() + days);
    return iso(d);
}

export function mondayOf(date: Date): string {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return iso(d);
}

// The last 13 weeks, oldest first, ending with this one
export function recentWeeks(current: string): string[] {
    const weeks = [];
    for(let i = WEEK_COUNT - 1; i >= 0; i--) {
        weeks.push(addDays(current, -7 * i));
    }
    return weeks;
}

export function weekLabel(week: string): string {
    const d = parse(week);
    return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

export function weekRange(week: string): string {
    return `${weekLabel(week)} – ${weekLabel(addDays(week, 6))}`;
}

// ISO week of the year (1-53): the week belongs to the year its Thursday falls in. In UTC, so daylight saving
// can't shift the day count.
export function weekNumber(week: string): number {
    const [ y, m, d ] = week.split('-').map(Number);
    const thursday = new Date(Date.UTC(y, m - 1, d + 3));
    const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
    return Math.ceil(((thursday.getTime() - yearStart) / 86400000 + 1) / 7);
}

// The week a team's numbers are asked for (its due week): ReportWeek 'last' (the default) is the week just finished,
// e.g. due Monday 9 AM for the week before; 'current' is the week they're due in (API sql/scoreboard-report-week.sql)
export function dueWeek(reportWeek: string | null | undefined): string {
    const thisWeek = mondayOf(new Date());
    return reportWeek == 'current' ? thisWeek : addDays(thisWeek, -7);
}

// The due week across metrics from several teams (My numbers, Employee Home): the current week only when every one
// of their teams reports it
export function dueWeekOf(metrics: any[]): string {
    return dueWeek(metrics.length && metrics.every((m: any) => m.ReportWeek == 'current') ? 'current' : 'last');
}

// "This week", "Last week", or the date
export function relativeWeekLabel(week: string): string {
    const thisWeek = mondayOf(new Date());
    return week == thisWeek ? 'This week' : week == addDays(thisWeek, -7) ? 'Last week' : weekLabel(week);
}

// "Wk 43 · Oct 19 – Oct 25", for entering a week's numbers
export function weekRangeNumbered(week: string): string {
    return `Wk ${weekNumber(week)} · ${weekRange(week)}`;
}

// The goal a week is judged against: the latest one starting on or before it, or the first goal for
// weeks before any (same rule as the API)
export function goalAt(metric: any, week: string): any {
    const goals = metric.Goals || [];
    let found = goals[0] || null;
    for(const g of goals) {
        if(g.FromWeek <= week) {
            found = g;
        }
    }
    return found;
}

// true / false against that week's goal, or null when there's no number
export function passes(metric: any, value: any, week: string): boolean | null {
    if(value === null || value === undefined || value === '') {
        return null;
    }
    const v = Number(value);
    const g = goalAt(metric, week);
    if(!g || !Number.isFinite(v)) {
        return null;
    }
    if(g.Cmp == 'gte') return v >= g.Goal;
    if(g.Cmp == 'lte') return v <= g.Goal;
    if(g.Cmp == 'range') return v >= g.Goal && v <= g.GoalMax;
    return v == g.Goal;
}

export function formatValue(metric: any, value: any): string {
    if(value === null || value === undefined || value === '') {
        return '';
    }
    const n = Number(value);
    const s = Math.abs(n) >= 1000 ? Math.round(n).toLocaleString() : String(Math.round(n * 100) / 100);
    const unit = metric.Unit || '';
    if(unit == '$K') return `$${s}K`;
    if(unit == '$') return `$${s}`;
    if(unit == '%') return `${s}%`;
    return unit ? `${s} ${unit}` : s;
}

// The compact form for cards, grid cells and chart axes: 950, 12.5K, 100K, 1.2M (3 significant
// digits), with only a one-character unit ($ or %). A longer custom unit ("quotes", "min") is left off
// to save room; $K is shown as dollars.
export function formatShort(metric: any, value: any): string {
    if(value === null || value === undefined || value === '') {
        return '';
    }
    let n = Number(value);
    let unit = metric.Unit || '';
    if(unit == '$K') {
        n *= 1000;
        unit = '$';
    }
    let s = String(Math.round(n * 100) / 100);
    const suffixes = [ 'K', 'M', 'B' ];
    for(let i = suffixes.length - 1; i >= 0; i--) {
        const scaled = Number((n / Math.pow(1000, i + 1)).toPrecision(3));
        // Picks the largest suffix the rounded number reaches (999,999 is 1M, not 1000K)
        if(Math.abs(scaled) >= 1) {
            s = String(scaled) + suffixes[i];
            break;
        }
    }
    if(unit == '$') return `$${s}`;
    if(unit == '%') return `${s}%`;
    return unit.length == 1 ? `${s}${unit}` : s;
}

// short: uses formatShort, for cards and the grid
export function goalText(metric: any, week: string, short = false): string {
    const g = goalAt(metric, week);
    if(!g) {
        return '';
    }
    const fmt = short ? formatShort : formatValue;
    if(g.Cmp == 'range') {
        return `${fmt(metric, g.Goal)}–${fmt(metric, g.GoalMax)}`;
    }
    return (g.Cmp == 'gte' ? '≥ ' : g.Cmp == 'lte' ? '≤ ' : '= ') + fmt(metric, g.Goal);
}

// Average of the numbers entered in these weeks, or null
export function average(metric: any, weeks: string[]): number | null {
    const values = weeks.map(w => metric.Values[w]).filter(v => v !== undefined && v !== null);
    if(!values.length) {
        return null;
    }
    return Math.round(values.reduce((a: number, b: number) => a + b, 0) / values.length * 10) / 10;
}

// Weeks in a row off goal, counting back from this week (or from last week while this week's number
// isn't in yet)
export function offStreak(metric: any, weeks: string[]): number {
    let n = 0;
    for(let i = weeks.length - 1; i >= 0; i--) {
        const v = metric.Values[weeks[i]];
        if(v === undefined || v === null) {
            if(i == weeks.length - 1) {
                continue;
            }
            break;
        }
        if(passes(metric, v, weeks[i]) === false) {
            n++;
        } else {
            break;
        }
    }
    return n;
}

// 0-100, how close a number is to its goal (full when on goal)
export function progress(metric: any, value: any, week: string): number {
    if(value === null || value === undefined) {
        return 0;
    }
    const v = Number(value);
    const g = goalAt(metric, week);
    if(!g) {
        return 0;
    }
    if(passes(metric, v, week)) {
        return 100;
    }
    if(g.Cmp == 'range') {
        const miss = Math.min(Math.abs(v - g.Goal), Math.abs(v - g.GoalMax));
        return Math.max(0, Math.round(100 - miss / Math.max(1, Math.abs(g.GoalMax - g.Goal)) * 100));
    }
    if(g.Cmp == 'lte') {
        return Math.max(0, Math.round(100 - (v - g.Goal) / Math.max(1, Math.abs(g.Goal)) * 100));
    }
    if(g.Goal == 0) {
        return 0;
    }
    return Math.max(0, Math.min(100, Math.round(v / g.Goal * 100)));
}

export function initials(name: string): string {
    const parts = String(name || '').trim().split(/\s+/).filter(p => p);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

// "Monday 9:00 AM"
export function dueText(day: number, time: string): string {
    const [ h, m ] = String(time || '09:00').split(':').map(Number);
    const hour = h % 12 || 12;
    return `${DAY_NAMES[day] || 'Monday'} ${hour}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function escapeHtml(s: string): string {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// A metric's Issue, as a meeting board item to start from (QuickAddAgendaItem's preset): the week's number against
// its goal in the name, and the owner, recent weeks, streak and definition in the description. Assigned to the owner.
export function metricIssue(metric: any, week: string, ownerName: string): { Name: string, Description: string, AssigneeEmployeeID: any } {
    const value = metric.Values[week];
    const entered = value !== undefined && value !== null;
    const goal = goalText(metric, week);
    const p = passes(metric, value, week);
    const weeks = recentWeeks(week);
    const avg = average(metric, weeks.slice(-4));
    const streak = offStreak(metric, weeks);
    // The four weeks before this one that have a number
    const earlier = weeks.slice(0, -1).filter(w => metric.Values[w] !== undefined && metric.Values[w] !== null).slice(-4);

    const name = `${metric.Name}: ${entered ? formatValue(metric, value) : 'not entered'}` + (goal ? (entered ? ` vs goal ${goal}` : ` (goal ${goal})`) : '');
    const lines = [
        `Week of ${weekRange(week)}: <strong>${entered ? escapeHtml(formatValue(metric, value)) : 'not entered'}</strong>${p === null ? '' : p ? ', on goal' : ', off goal'}`,
        goal ? `Goal: ${escapeHtml(goal)}` : '',
        avg === null ? '' : `4-week average: ${escapeHtml(formatValue(metric, avg))}`,
        streak >= 2 ? `Off goal ${streak} weeks in a row` : '',
        earlier.length ? `Before that: ${earlier.map(w => `${weekLabel(w)} ${escapeHtml(formatValue(metric, metric.Values[w]))}`).join(' · ')}` : ''
    ].filter(l => l);
    const description = `<p><strong>${escapeHtml(teamLabel(metric.Team))} scorecard</strong> · Owner: ${escapeHtml(ownerName)}</p>`
        + `<ul>${lines.map(l => `<li><p>${l}</p></li>`).join('')}</ul>`
        + (metric.Definition ? `<p>${escapeHtml(metric.Definition)}</p>` : '');
    return { Name: name, Description: description, AssigneeEmployeeID: metric.OwnerEmployeeID || null };
}
